"use client";
import * as React from "react";
import { PlaceCard } from "./PlaceCard";
import { IPlace } from "@/interfaces/property";
import { useImageLoadingThreshold } from "@/hooks/use-image-loading-threshold";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { useTranslation } from "@/lib/i18n";

interface PlacesListProps {
  places: IPlace[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onPlaceSelect?: (place: IPlace) => void;
  onPlaceHover?: (placeId: string | null) => void;
}

export const PlacesList: React.FC<PlacesListProps> = ({
  places,
  hasMore,
  loading,
  onLoadMore,
  onPlaceSelect,
  onPlaceHover
}) => {
  const { t } = useTranslation();
  
  const { threshold } = useImageLoadingThreshold({
    componentType: 'places',
    buffer: 2, // Load 2 extra images for smooth scrolling
    considerHeaderHeight: 80, // Account for header height
  });

  const loadingRef = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore,
    threshold: 200, // Load more when 200px from bottom for smoother experience
  });

  if (places.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center w-full py-8">
        <p className="text-tertiary text-sm">{t('places.no_places_found')}</p>
      </div>
    );
  }

  return (
    <>
      <section id="Places" aria-label="Places"
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 
        gap-x-[8px] md:gap-x-[12px] gap-y-[16px] md:gap-y-[24px] w-full md:w-[430px] md:mx-[-14px]">
        {places.map((place, index) => (
          <PlaceCard 
            key={`${place.id}-${index}`} 
            index={index} 
            place={place}
            loading={index < threshold ? 'eager' : 'lazy'} // Dynamic threshold based on viewport
            onClick={() => onPlaceSelect?.(place)}
            onMouseEnter={() => onPlaceHover?.(place.id)}
            onMouseLeave={() => onPlaceHover?.(null)}
          />
        ))}
      </section>
      
      {/* Loading indicator for infinite scroll - always render to ensure intersection observer works */}
      <div 
        ref={loadingRef}
        className="flex justify-center w-full py-4 min-h-[40px]"
      >
        {loading && hasMore && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-tertiary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-tertiary text-sm">{t('places.loading_more')}</span>
          </div>
        )}
        {!loading && hasMore && places.length > 0 && (
          <div className="flex items-center gap-2 opacity-50">
            <div className="w-3 h-3 border border-tertiary rounded-full"></div>
            <span className="text-tertiary text-xs">{t('places.preloading')}</span>
          </div>
        )}
        {/*!hasMore && places.length > 0 && (
          <span className="text-tertiary text-sm">{t('places.no_more_places')}</span>
        )*/}
        {/* Invisible placeholder when no content to prevent layout shift */}
        {!loading && hasMore && (
          <div className="invisible">
            <div className="w-4 h-4 border-2 border-tertiary border-t-transparent rounded-full"></div>
            <span className="text-tertiary text-sm">{t('places.loading_more')}</span>
          </div>
        )}
      </div>
    </>
  );
};
